import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2, Save, Loader2, Search, Package } from 'lucide-react';
import { stockAdjustmentService, productService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { usePermissions } from '../../../../hooks/usePermissions';
import { MODULES, ACTIONS } from '../../../../utils/permissions';
import { Toast } from '../../../../components';
import './StockAdjustmentCreatePage.css';

interface AdjustmentItem {
  product_id: number;
  product_name?: string;
  product_sku?: string;
  physical_stock: number;
  digital_stock: number;
  adjustment_quantity: number;
  notes?: string;
}

const StockAdjustmentCreatePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(MODULES.STOCK, ACTIONS.STOCK_ADJUSTMENT_CREATE);
  const canApprove = hasPermission(MODULES.STOCK, ACTIONS.STOCK_ADJUSTMENT_APPROVE);
  const canEditRequest = canCreate || canApprove;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<AdjustmentItem[]>([]);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  
  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const loadRequest = useCallback(async () => {
    if (!id || id === 'new') return;
    setLoading(true);
    try {
      const response = await stockAdjustmentService.getById(id);
      if (response.success && response.data) {
        const request = response.data;

        if (request.status !== 'pending') {
          showToast('Apenas pedidos pendentes podem ser editados', 'warning');
          navigate(`/erp/stock/adjustments/${id}`);
          return;
        }

        if (!canEditRequest) {
          showToast('Você não tem permissão para editar este pedido', 'error');
          navigate(`/erp/stock/adjustments/${id}`);
          return;
        }

        setReason(request.reason || '');
        setNotes(request.notes || '');
        if (request.items) {
          setItems(request.items.map((item: any) => ({
            product_id: item.product_id,
            product_name: item.product_name,
            product_sku: item.product_sku,
            physical_stock: item.physical_stock,
            digital_stock: item.digital_stock,
            adjustment_quantity: item.adjustment_quantity,
            notes: item.notes
          })));
        }
      }
    } catch (error: any) {
      showToast('Erro ao carregar pedido', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showToast, navigate, canEditRequest]);

  useEffect(() => {
    if (id && id !== 'new') {
      loadRequest();
    }
  }, [id, loadRequest]);

  useEffect(() => {
    if (productSearch.length >= 2) {
      const timer = setTimeout(() => {
        searchProducts(productSearch);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setProductResults([]);
    }
  }, [productSearch]);

  const searchProducts = async (term: string) => {
    setSearchLoading(true);
    try {
      const response = await productService.getAll({ search: term, active: true });
      if (response.success) {
        const filtered = (response.data || [])
          .filter((product: any) => 
            product.name?.toLowerCase().includes(term.toLowerCase()) ||
            product.sku?.toLowerCase().includes(term.toLowerCase()) ||
            product.barcode?.toLowerCase().includes(term.toLowerCase())
          )
          .slice(0, 10);
        setProductResults(filtered);
      }
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const addProduct = (product: any) => {
    if (items.find(item => item.product_id === product.id)) {
      showToast('Produto já adicionado', 'error');
      return;
    }

    const digitalStock = product.stock_quantity || 0;
    const newItem: AdjustmentItem = {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      physical_stock: 0,
      digital_stock: digitalStock,
      adjustment_quantity: 0,
      notes: ''
    };
    setItems([...items, newItem]);
    setProductSearch('');
    setProductResults([]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof AdjustmentItem, value: any) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    
    // Calcular adjustment_quantity automaticamente
    if (field === 'physical_stock' || field === 'digital_stock') {
      const physical = field === 'physical_stock' ? value : updated[index].physical_stock;
      const digital = field === 'digital_stock' ? value : updated[index].digital_stock;
      updated[index].adjustment_quantity = physical - digital;
    }
    
    setItems(updated);
  };

  const handleSave = async () => {
    if (items.length === 0) {
      showToast('Adicione pelo menos um item', 'error');
      return;
    }

    setSaving(true);
    try {
      const data = {
        reason,
        notes,
        items: items.map(item => ({
          product_id: item.product_id,
          physical_stock: item.physical_stock,
          digital_stock: item.digital_stock,
          adjustment_quantity: item.adjustment_quantity,
          notes: item.notes
        }))
      };

      let response;
      if (id && id !== 'new') {
        response = await stockAdjustmentService.update(id, data);
      } else {
        response = await stockAdjustmentService.create(data);
      }

      if (response.success) {
        const savedId = id && id !== 'new' ? id : response.data?.id;
        showToast(id && id !== 'new' ? 'Pedido atualizado com sucesso' : 'Pedido criado com sucesso', 'success');
        setTimeout(() => {
          if (savedId) {
            navigate(`/erp/stock/adjustments/${savedId}`);
          } else {
            navigate('/erp/stock/adjustments');
          }
        }, 1000);
      } else {
        showToast(response.message || 'Erro ao salvar pedido', 'error');
      }
    } catch (error: any) {
      showToast('Erro ao salvar pedido', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="stock-adjustment-create-loading">
        <Loader2 size={32} className="spinner" />
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="stock-adjustment-create">
      <div className="stock-adjustment-create-header">
        <button className="stock-adjustment-create-back" onClick={() => navigate('/erp/stock/adjustments')}>
          <ArrowLeft size={20} />
        </button>
        <h1>{id && id !== 'new' ? 'Editar Pedido de Ajuste' : 'Novo Pedido de Ajuste'}</h1>
      </div>

      <div className="stock-adjustment-create-content">
        {/* Form Fields */}
        <div className="stock-adjustment-create-form">
          <div className="stock-adjustment-create-field">
            <label>Motivo do Ajuste</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Divergência entre estoque físico e digital"
            />
          </div>

          <div className="stock-adjustment-create-field">
            <label>Observações</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observações adicionais..."
              rows={3}
            />
          </div>
        </div>

        {/* Product Search */}
        <div className="stock-adjustment-create-search">
          <div className="stock-adjustment-create-search-wrapper">
            <Search size={20} className="stock-adjustment-create-search-icon" />
            <input
              type="text"
              placeholder="Buscar produto por nome, SKU ou código de barras..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
            />
            {searchLoading && <Loader2 size={20} className="spinner" />}
          </div>

          {productResults.length > 0 && (
            <div className="stock-adjustment-create-results">
              {productResults.map((product) => (
                <div
                  key={product.id}
                  className="stock-adjustment-create-result-item"
                  onClick={() => addProduct(product)}
                >
                  <div>
                    <p className="stock-adjustment-create-result-name">{product.name}</p>
                    <p className="stock-adjustment-create-result-sku">SKU: {product.sku || 'N/A'}</p>
                  </div>
                  <Package size={20} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Items List */}
        <div className="stock-adjustment-create-items">
          <h3>Itens do Ajuste ({items.length})</h3>
          
          {items.length === 0 ? (
            <div className="stock-adjustment-create-empty">
              <Package size={48} />
              <p>Nenhum item adicionado</p>
            </div>
          ) : (
            <div className="stock-adjustment-create-items-list">
              {items.map((item, index) => (
                <div key={index} className="stock-adjustment-create-item">
                  <div className="stock-adjustment-create-item-header">
                    <div>
                      <p className="stock-adjustment-create-item-name">{item.product_name}</p>
                      <p className="stock-adjustment-create-item-sku">SKU: {item.product_sku || 'N/A'}</p>
                    </div>
                    <button onClick={() => removeItem(index)} className="stock-adjustment-create-item-remove">
                      <Trash2 size={18} />
                    </button>
                  </div>

                  <div className="stock-adjustment-create-item-fields">
                    <div className="stock-adjustment-create-item-field">
                      <label>Estoque Físico</label>
                      <input
                        type="number"
                        min="0"
                        value={item.physical_stock}
                        onChange={(e) => updateItem(index, 'physical_stock', parseInt(e.target.value) || 0)}
                      />
                    </div>

                    <div className="stock-adjustment-create-item-field">
                      <label>Estoque Digital</label>
                      <input
                        type="number"
                        min="0"
                        value={item.digital_stock}
                        onChange={(e) => updateItem(index, 'digital_stock', parseInt(e.target.value) || 0)}
                        readOnly
                      />
                    </div>

                    <div className="stock-adjustment-create-item-field">
                      <label>Ajuste</label>
                      <input
                        type="number"
                        value={item.adjustment_quantity}
                        readOnly
                        className={item.adjustment_quantity > 0 ? 'positive' : item.adjustment_quantity < 0 ? 'negative' : ''}
                      />
                    </div>
                  </div>

                  <div className="stock-adjustment-create-item-field">
                    <label>Observações</label>
                    <input
                      type="text"
                      value={item.notes || ''}
                      onChange={(e) => updateItem(index, 'notes', e.target.value)}
                      placeholder="Observações sobre este item..."
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="stock-adjustment-create-actions">
          <button onClick={() => navigate('/erp/stock/adjustments')} className="stock-adjustment-create-cancel">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving || items.length === 0} className="stock-adjustment-create-save">
            {saving ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
            {saving ? 'Salvando...' : 'Salvar Pedido'}
          </button>
        </div>
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default StockAdjustmentCreatePage;
